import {
  ContentfulCollection,
  ContentfulGenericPage,
  ContentfulIcon,
  ContentfulNav,
  ContentfulNavDividerAndHeading,
  ContentfulNavMenu,
} from '@jbhi-fi/apis/src/contentful/types';
import { HeaderNavigationProps } from '../../HeaderLayout/components/HeaderNavigation/HeaderNavigation';
import { MenuItemWithSubMenu, BaseMenuItem, MenuItemType } from '../../Menu/types';
import { IconName } from '@jbhi-fi/lanyard-ui/components/Icon';

export abstract class NavTransformer {
  static hasFields = (
    item: ContentfulNavMenu | ContentfulNavDividerAndHeading | ContentfulCollection | ContentfulGenericPage
  ): boolean => Boolean(item.fields);

  static getIcon = (icon?: ContentfulIcon | IconName): string | null => {
    const iconName = typeof icon === 'string' ? icon : icon?.fields?.name || '';
    return iconName ? iconName : null;
  };

  static handleNavDividerAndHeading = (
    item: ContentfulNavDividerAndHeading
  ): MenuItemWithSubMenu<MenuItemWithSubMenu<BaseMenuItem>> => {
    const { mainCollection, shouldDisplayHeading, heading } = item.fields ?? {};

    if (mainCollection) {
      return {
        ...this.handleCollection(mainCollection),
        type: MenuItemType.dividerWithCollection,
        label: shouldDisplayHeading ? heading : mainCollection.fields?.title,
        isBold: true,
        isMainCollection: true,
      };
    }

    if (shouldDisplayHeading) {
      return {
        type: MenuItemType.headingAndDivider,
        label: heading,
      };
    }

    return {
      type: MenuItemType.divider,
    };
  };

  private static _getParentHandle = (item: ContentfulCollection): string | null => {
    if (!item.fields?.nestedCollection) return null;
    const parent = item.fields?.ancestorCollections?.[0];

    if (parent?.fields?.nestedCollection) {
      return this._getParentHandle(parent);
    }

    return parent?.fields?.handle ?? null;
  };

  private static _getNavMenuURL(
    url: string,
    mainCollection: ContentfulCollection | undefined,
    topSubMenu: BaseMenuItem[],
    subMenu: BaseMenuItem[]
  ): string {
    if (url?.trim()) return url.trim();
    if (mainCollection && topSubMenu.length === 0 && subMenu.length === 0) {
      const parentHandle = this._getParentHandle(mainCollection);
      return `/collections/${parentHandle ? `${parentHandle}/` : ''}${mainCollection.fields?.handle}`;
    }
    return '';
  }

  static handleCollection = (item: ContentfulCollection): MenuItemWithSubMenu<MenuItemWithSubMenu<BaseMenuItem>> => {
    const parentHandle = this._getParentHandle(item);
    return {
      label: item.fields?.title,
      href: `/collections/${parentHandle ? `${parentHandle}/` : ''}${item.fields?.handle}`,
      type: MenuItemType.item,
      icon: this.getIcon(item.fields?.icon),
      isBold: false,
    };
  };

  static handleGenericPage = (item: ContentfulGenericPage): MenuItemWithSubMenu<MenuItemWithSubMenu<BaseMenuItem>> => {
    return {
      label: item.fields?.heading,
      href: `/pages/${item.fields?.urlSlug}`,
      type: MenuItemType.item,
      isBold: false,
    };
  };

  static handleNavMenu = (item: ContentfulNavMenu): MenuItemWithSubMenu<MenuItemWithSubMenu<BaseMenuItem>> => {
    const {
      url = '',
      heading = '',
      icon,
      topNavMenuItems,
      navMenuItems,
      openInNewWindow,
      isHeadingBold,
      mainCollection,
    } = (item as ContentfulNavMenu).fields ?? {};

    const defaultSubMenu: Array<BaseMenuItem> = mainCollection
      ? [
          {
            ...this.handleCollection(mainCollection),
            label: `${mainCollection.fields?.title ?? ''}`,
            isBold: true,
            icon: null,
            isMainCollection: true,
            isTopMenu: true,
          },
        ]
      : [];
    const topSubMenu = topNavMenuItems
      ? topNavMenuItems.filter(this.hasFields).map(menuItem => ({
          ...this.parseContentfulNavMenu(menuItem),
          isTopMenu: true,
        }))
      : [];

    const subMenu = navMenuItems ? navMenuItems.filter(this.hasFields).map(this.parseContentfulNavMenu) : [];

    const subMenuExists = topSubMenu.length > 0 || subMenu.length > 0;

    return {
      type: MenuItemType.item,
      href: this._getNavMenuURL(url, mainCollection, topSubMenu, subMenu),
      subMenu: subMenuExists ? [...defaultSubMenu, ...topSubMenu, ...subMenu] : [],
      shouldOpenInNewWindow: openInNewWindow,
      label: heading,
      icon: this.getIcon(icon),
      isBold: isHeadingBold,
    };
  };

  static parseContentfulNavMenu = (
    item: ContentfulNavMenu | ContentfulNavDividerAndHeading | ContentfulCollection | ContentfulGenericPage
  ): MenuItemWithSubMenu<MenuItemWithSubMenu<BaseMenuItem>> => {
    if (item.sys.contentType.sys.id === 'navDividerAndHeading') {
      return this.handleNavDividerAndHeading(item as ContentfulNavDividerAndHeading);
    }

    if (item.sys.contentType.sys.id === 'smartCollection') {
      return this.handleCollection(item as ContentfulCollection);
    }

    if (item.sys.contentType.sys.id === 'genericPage') {
      return this.handleGenericPage(item as ContentfulGenericPage);
    }

    return this.handleNavMenu(item as ContentfulNavMenu);
  };

  static parseMobileNavMenuItem = (item: ContentfulNavMenu): BaseMenuItem => {
    return {
      type: MenuItemType.item,
      label: item.fields?.heading,
      href: item.fields?.url,
      icon: this.getIcon(item.fields?.icon),
    };
  };

  static toHeaderNavigationProps = ({ fields }: ContentfulNav): HeaderNavigationProps => {
    const mobileNavMenuItems = fields?.mobileNavMenuItems ?? [];
    const navOptions = fields?.navMenuItems.map(item => {
      const { heading = '', navMenuItems = [], url } = item?.fields ?? {};
      return {
        label: heading,
        href: url,
        menuItems: navMenuItems.filter(this.hasFields).map(this.parseContentfulNavMenu),
      };
    });

    return {
      menuItems: navOptions ?? [],
      mobileNavMenuItems: mobileNavMenuItems.filter(this.hasFields)?.map(this.parseMobileNavMenuItem),
    };
  };
}
